import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme } from './use-theme';

/**
 * A chave do modo escuro: duas posições, claro e escuro.
 *
 * O aparelho só decide **como o sistema abre** na primeira vez — sem escolha gravada, a
 * tela nasce no tema do sistema operacional. A partir do primeiro toque a chave manda, e
 * "seguir o aparelho" não é uma terceira posição: a pessoa quer escuro ou claro, e uma
 * terceira opção só obrigaria a pensar qual das três está ligada.
 *
 * `role="switch"` com `aria-checked`: o leitor de tela diz "Modo escuro, ligado".
 * `compact` é a versão do menu recolhido — o ícone sozinho, com o mesmo nome.
 */
export function ThemeToggle({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  const { resolved, setPreference } = useTheme();
  const dark = resolved === 'dark';
  const toggle = (): void => setPreference(dark ? 'light' : 'dark');
  const Icon = dark ? Moon : Sun;

  if (compact) {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={dark}
        aria-label="Modo escuro"
        title={dark ? 'Modo escuro ligado' : 'Modo escuro desligado'}
        onClick={toggle}
        className={cn(
          'flex size-10 items-center justify-center rounded-md text-(--color-content-muted) transition-colors hover:bg-(--color-surface-raised) hover:text-(--color-content)',
          className,
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      onClick={toggle}
      className={cn(
        // No dedo, 44px; no monitor, a linha do rodapé.
        'flex min-h-11 w-full items-center justify-between gap-3 rounded-md px-2 text-sm transition-colors hover:bg-(--color-surface-raised) md:min-h-9',
        className,
      )}
    >
      <span className="flex items-center gap-2.5 text-(--color-content-muted)">
        <Icon className="size-4" aria-hidden="true" />
        <span className="text-(--color-content)">Modo escuro</span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          dark ? 'bg-(--color-brand)' : 'bg-(--color-border-subtle)',
        )}
      >
        <span
          className={cn(
            'absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none',
            dark && 'translate-x-4',
          )}
        />
      </span>
    </button>
  );
}
